import { OU_CODE_SEPARATOR, ouCodeKey } from './ouCode';
import { OrganizationalUnitViewer } from '90.quickConnect.Models/models/user/organizationalUnitViewer';

/** UO de rattachement de l'utilisateur, racine de l'arbre */
export type AttachedOrganizationalUnit = {
  id: string;
  code: string;
  name: string;
};

/**
 * Arbre des UO accessibles à l'utilisateur (UO de visibilité + UO de rattachement), enraciné sur l'UO de rattachement.
 * Portage de OrganizationalUnitTree Android : chaque UO est rattachée à son plus proche ancêtre accessible, les codes
 * sont comparés normalisés (casse, espaces, séparateurs) et une UO reçue plusieurs fois n'apparaît qu'une fois.
 */
export class OrganizationalUnitTree {
  /** Noeuds indexés par ouCodeKey ; le noeud garde le code tel que reçu */
  private readonly nodesByKey = new Map<string, OrganizationalUnitViewer>();

  private readonly roots: OrganizationalUnitViewer[] = [];

  private readonly attachedKey: string;

  private constructor(attachedCode: string) {
    this.attachedKey = ouCodeKey(attachedCode);
  }

  static build(attached: AttachedOrganizationalUnit, units: OrganizationalUnitViewer[]): OrganizationalUnitTree {
    const tree = new OrganizationalUnitTree(attached.code);

    // Parcours trié : le choix entre 2 écritures d'une même UO ne dépend pas de l'ordre reçu
    [...units]
      .sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''))
      .forEach((unit) => {
        const key = ouCodeKey(unit.code);
        if (!key) return;
        const existing = tree.nodesByKey.get(key);
        // Même UO déjà présente : on garde la première, sauf si seule la nouvelle a un logo
        if (!existing || (!existing.logo && unit.logo)) tree.nodesByKey.set(key, { ...unit, children: [] });
      });

    // L'UO de rattachement est toujours présente
    if (tree.attachedKey && !tree.nodesByKey.has(tree.attachedKey)) {
      tree.nodesByKey.set(tree.attachedKey, {
        id: attached.id,
        code: attached.code,
        name: attached.name,
        fullName: attached.name,
        logo: '',
        isRattachment: true,
        children: [],
      });
    }

    // Tri par clé : les parents précèdent leurs enfants et les frères sont ordonnés
    const orphans: OrganizationalUnitViewer[] = [];
    tree.getEntries().forEach(([key, node]) => {
      if (key === tree.attachedKey) return;
      const parent = tree.findNearestAncestor(key);
      // Hors de la branche de rattachement : affichée à la racine plutôt que perdue
      if (parent) parent.children.push(node);
      else orphans.push(node);
    });

    const attachedNode = tree.nodesByKey.get(tree.attachedKey);
    if (attachedNode) tree.roots.push(attachedNode);
    tree.roots.push(...orphans);
    return tree;
  }

  /** Noeuds avec leur clé, triés par clé : les parents précèdent leurs enfants */
  private getEntries(): [string, OrganizationalUnitViewer][] {
    return [...this.nodesByKey.entries()].sort(([a], [b]) => a.localeCompare(b));
  }

  /** Plus proche ancêtre présent dans l'arbre (clé de code) */
  private findNearestAncestor(key: string): OrganizationalUnitViewer | undefined {
    for (const ancestorKey of OrganizationalUnitTree.ancestorKeys(key)) {
      const ancestor = this.nodesByKey.get(ancestorKey);
      if (ancestor) return ancestor;
    }
    return undefined;
  }

  /** Clés des ancêtres d'une clé de code, du plus proche au plus lointain */
  private static ancestorKeys(key: string): string[] {
    const parts = key.split(OU_CODE_SEPARATOR);
    return parts.slice(0, -1).map((_, index) => parts.slice(0, parts.length - 1 - index).join(OU_CODE_SEPARATOR));
  }

  getRoots(): OrganizationalUnitViewer[] {
    return this.roots;
  }

  getNode(code: string | null | undefined): OrganizationalUnitViewer | undefined {
    return this.nodesByKey.get(ouCodeKey(code));
  }

  /** L'UO fait-elle partie de l'arbre (UO visible ou de rattachement) */
  contains(code: string | null | undefined): boolean {
    return !!this.getNode(code);
  }

  /** Nombre d'UO distinctes de l'arbre, UO de rattachement incluse */
  getOuCount(): number {
    return this.nodesByKey.size;
  }

  /** Toutes les UO de l'arbre, triées par code */
  getUnits(): OrganizationalUnitViewer[] {
    return this.getEntries().map(([, node]) => node);
  }

  isAttachedOu(code: string | null | undefined): boolean {
    return !!this.attachedKey && ouCodeKey(code) === this.attachedKey;
  }

  /**
   * UO accessible vers laquelle basculer pour une ressource de l'UO donnée : l'UO elle-même si elle est accessible,
   * sinon son descendant accessible le plus proche (à égalité de profondeur, le premier par ordre de code)
   */
  getTargetOuToSwitch(code: string | null | undefined): OrganizationalUnitViewer | undefined {
    const direct = this.getNode(code);
    if (direct) return direct;

    const targetKey = ouCodeKey(code);
    if (!targetKey) return undefined;

    const prefix = `${targetKey}${OU_CODE_SEPARATOR}`;
    const depth = (key: string) => key.split(OU_CODE_SEPARATOR).length;
    const [nearest] = this.getEntries()
      .filter(([key]) => key.startsWith(prefix))
      .sort(([a], [b]) => depth(a) - depth(b));
    return nearest?.[1];
  }

  /**
   * UO dont provient le logo affiché pour l'UO donnée : elle-même si elle a un logo, sinon son plus proche ancêtre qui
   * en a un, sans dépasser l'UO de rattachement. undefined => repli sur le logo de l'UO de rattachement
   */
  findLogoOwner(code: string | null | undefined): OrganizationalUnitViewer | undefined {
    const node = this.getNode(code);
    if (!node) return undefined;
    if (node.logo) return node;

    const key = ouCodeKey(code);
    if (key === this.attachedKey) return undefined;
    for (const ancestorKey of OrganizationalUnitTree.ancestorKeys(key)) {
      const ancestor = this.nodesByKey.get(ancestorKey);
      if (ancestor?.logo) return ancestor;
      if (ancestorKey === this.attachedKey) return undefined;
    }
    return undefined;
  }

  /** Codes à déplier pour rendre visible l'UO donnée : ses ancêtres, plus les racines qui ont des enfants */
  getCodesToExpandFor(code: string | null | undefined): string[] {
    const rootCodes = this.roots.filter((root) => root.children.length > 0).map((root) => root.code);
    const key = ouCodeKey(code);
    const ancestorCodes = this.getNode(code)
      ? OrganizationalUnitTree.ancestorKeys(key)
          .map((ancestorKey) => this.nodesByKey.get(ancestorKey)?.code)
          .filter((ancestorCode): ancestorCode is string => !!ancestorCode)
      : [];
    return [...new Set([...rootCodes, ...ancestorCodes])];
  }
}
